import React, { useEffect } from "react";
import { AppSidebar } from "@/components/ui/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Link, Outlet, useMatches } from "react-router-dom";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

export default function DashboardLayout() {
  const matches = useMatches();
  const { t } = useTranslation();

  useEffect(() => {
    // Apply overflow-hidden to body when component mounts
    document.body.style.overflow = "hidden";

    // Cleanup function to remove overflow-hidden when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Translation map for breadcrumb keys
  const breadcrumbTranslations: Record<string, string> = {
    "Dashboard": t("common.dashboard"),
    "Overview": t("common.overview"),
    "Members": t("pages.members.title"),
    "Districts": t("nav.districts"),
    "Zones": t("nav.sectors"),
    "Saving Seasons": t("nav.savingCircles"),
    "Saving Groups": t("nav.savingGroups"),
    "Contributions": t("pages.contributions.title"),
    "Loans": t("pages.loans.title"),
    "Fines & Penalties": t("pages.fines.title"),
    "Expenses & Bills": t("pages.expenses.title"),
    "Attendance": t("pages.attendance.title"),
    "Contributions reports": t("nav.contributionsReports"),
    "Loans reports": t("nav.loansReports"),
    "General season reports": t("nav.generalSeasonReports"),
    "Expense reports": t("nav.expenseReports"),
    "Settings": t("nav.settings"),
    "Account settings": t("nav.accountSettings"),
    "Expenses Categories": t("nav.expenseCategories"),
    "Payment Methods": t("nav.paymentMethods"),
    "System Users": t("nav.systemUsers"),
    "Roles & permissions": t("nav.rolePermissions"),
    "General settings": t("nav.generalInformation"),
  };

  let crumbs = matches
    // @ts-ignore
    .filter((match) => Boolean(match.handle?.crumb))
    .map((match) => {
      // @ts-ignore
      const crumbText = match.handle.crumb(match.data);
      return {
        crumb: breadcrumbTranslations[crumbText] || crumbText,
        pathname: match.pathname || "/dashboard",
      };
    });

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="overflow-x-auto bg-slate-100">
        <header className="flex sticky top-0 z-50 bg-white h-14 justify-between border-b shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {crumbs.map((crumb, index) => {
                  const isLast = index === crumbs.length - 1;
                  return isLast ? (
                    <BreadcrumbItem key={index}>
                      <BreadcrumbPage>{crumb.crumb}</BreadcrumbPage>
                    </BreadcrumbItem>
                  ) : (
                    <React.Fragment key={index}>
                      <BreadcrumbItem>
                        <Link to={crumb.pathname}>{crumb.crumb}</Link>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                    </React.Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="px-5">
            <LanguageSwitcher />
          </div>
        </header>
        {/* <ScrollArea className="h-full flex-1 w-full overflow-x-auto"> */}
        <div className="flex flex-1 flex-col gap-4 pt-0 p-2">
          <Outlet />
        </div>
        {/* </ScrollArea> */}
      </SidebarInset>
    </SidebarProvider>
  );
}
